import axios from 'axios'
import { getErrorMessage } from '@/utils/userErrors'
import { getApiOrigin } from '@/utils/apiBase'

const API_BASE_URL = getApiOrigin()

// Создаем инстанс axios для staff API
const staffApi = axios.create({
  baseURL: `${API_BASE_URL}/api/admin/staff/`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Helper function to get CSRF token from cookie
function getCookie(name) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

// Интерсепторы для обработки ошибок
staffApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Token ${token}`
    }

    // Добавляем CSRF токен из cookie
    const csrfToken = getCookie('csrftoken')
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken
    }

    // Для FormData убираем Content-Type чтобы браузер сам установил boundaries
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }


    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

staffApi.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user_name')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// API методы для персонала
export default {
  // Проверка доступа персонала
  async checkStaffAccess() {
    try {
      await staffApi.get('dashboard_stats/')
      return { hasAccess: true }
    } catch (error) {
      return {
        hasAccess: false,
        error: getErrorMessage(error, 'errors.forbidden')
      }
    }
  },

  // Dashboard статистика для персонала
  async getDashboardStats() {
    return staffApi.get('dashboard_stats/')
  },

  // === ЛОКАЦИИ ===

  // Получить все локации
  async getLocations({ activeClinicsOnly = false } = {}) {
    const params = activeClinicsOnly ? { active_clinics_only: true } : {}
    const response = await staffApi.get('locations/', { params })
    return { cities: response.data }
  },

  // Получить районы по городу
  async getDistricts(cityId = null) {
    const params = cityId ? { city: cityId, page_size: 100 } : { page_size: 100 }
    const response = await staffApi.get('../../districts/', { params })
    return response.data
  },

  // Создать район города
  async createDistrict(data) {
    return staffApi.post('../../districts/', data)
  },

  // Обновить район города
  async updateDistrict(id, data) {
    return staffApi.patch(`../../districts/${id}/`, data)
  },

  // Удалить район города
  async deleteDistrict(id) {
    return staffApi.delete(`../../districts/${id}/`)
  },

  // Создать новый город
  async createCity(name_kz, name_ru) {
    return staffApi.post('create_city/', {
      name_kz: name_kz,
      name_ru: name_ru
    })
  },

  // Обновить название города
  async updateCity(id, cityData) {
    return staffApi.put(`${id}/update_city/`, {
      name_kz: cityData.name_kz,
      name_ru: cityData.name_ru
    })
  },

  // Удалить город
  async deleteCity(id) {
    return staffApi.delete(`${id}/delete_city/`)
  },

  // Создать новую поликлинику
  async createClinic(data) {
    return staffApi.post('create_clinic/', data)
  },

  // Создать новый участок


  // === МЕДПЕРСОНАЛ ===

  // Получить список медперсонала
  async getMedicalStaff(params = {}) {
    const response = await staffApi.get('medical_staff/', { params })
    return response.data
  },

  // Получить список врачей организаций
  async getOrganizationDoctors(params = {}) {
    const response = await staffApi.get('organization_doctors/', { params })
    return response.data
  },

  // === ПЕРСОНАЛ (главврачи и администраторы учреждений) ===
  async getManagementUsers(params = {}) {
    const response = await staffApi.get('management_users/', { params })
    return response.data
  },
  async getStaffUserDetails(id) {
    const response = await staffApi.get(`${id}/staff_user_details/`)
    return response.data
  },
  async updateStaffUser(id, data) {
    return staffApi.put(`${id}/update_staff_user/`, data)
  },
  async deleteStaffUser(id) {
    return staffApi.delete(`${id}/delete_staff_user/`)
  },
  async setReport3Access(id, enabled) {
    const response = await staffApi.post(`${id}/report3_access/`, { enabled })
    return response.data
  },
  async createStaffUser(data) {
    return staffApi.post('create_staff_user/', data)
  },

  async lookupExistingUser(data) {
    const response = await staffApi.post('lookup_existing_user/', data)
    return response.data
  },

  // === ПАЦИЕНТЫ ===

  // Получить список пациентов
  async getPatients(params = {}) {
    const response = await staffApi.get('patients_list/', { params })
    return response.data  // Возвращаем полный ответ с пагинацией
  },

  async getPatientOrganizations(id) {
    return (await staffApi.get(`${id}/patient_organizations/`)).data
  },

  async updatePatientOrganizations(id, data) {
    return (await staffApi.put(`${id}/patient_organizations/`, data)).data
  },

  async getPatientOrganizationOptions(params = {}) {
    return (await staffApi.get('patient_organization_options/', { params })).data
  },

  // === СОЗДАНИЕ ПОЛЬЗОВАТЕЛЕЙ ===

  // Создать нового врача
  async createDoctor(data) {
    return staffApi.post('create_doctor/', data)
  },

  // Создать новую медсестру
  async createNurse(data) {
    return staffApi.post('create_nurse/', data)
  },

  // Создать нового пациента
  async createPatient(data) {
    return staffApi.post('create_patient/', data)
  },

  // === ОБНОВЛЕНИЕ ПОЛЬЗОВАТЕЛЕЙ ===

  // Обновить данные врача
  async updateDoctor(id, data) {
    return staffApi.put(`${id}/update_doctor/`, data)
  },

  // Обновить данные медсестры
  async updateNurse(id, data) {
    return staffApi.put(`${id}/update_nurse/`, data)
  },

  // Обновить данные пациента
  async updatePatient(id, data) {
    return staffApi.put(`${id}/update_patient/`, data)
  },

  // Обновить поликлинику
  async updateClinic(data, clinicId = null) {
    const id = clinicId ?? data.id
    return staffApi.put(`update_clinic/${id}`, data)
  },
  async deleteClinic(id) {
    return staffApi.delete(`${id}/delete_clinic/`)
  },
  async deleteDoctor(id) {
    return staffApi.delete(`${id}/delete_doctor/`)
  },
  async deleteNurse(id) {
    return staffApi.delete(`${id}/delete_nurse/`)
  },
  async deletePatient(id) {
    return staffApi.delete(`${id}/delete_patient/`)
  },
  // Обновить участок

  // === УДАЛЕНИЕ ПОЛЬЗОВАТЕЛЕЙ (УСТАРЕЛО - используйте деактивацию через update методы) ===

  // === ДЕАКТИВАЦИЯ ПОЛЬЗОВАТЕЛЕЙ ===
  // Для деактивации используйте соответствующие update методы с параметром { is_active: false }
  // Например: await staffApi.updateDoctor(id, { is_active: false })

  // === ПОЛУЧЕНИЕ ДАННЫХ ПОЛЬЗОВАТЕЛЕЙ ===

  // Получить подробные данные врача
  async getDoctorDetails(id) {
    const response = await staffApi.get(`${id}/doctor_details/`)
    return response.data
  },

  // Получить подробные данные медсестры
  async getNurseDetails(id) {
    const response = await staffApi.get(`${id}/nurse_details/`)
    return response.data
  },

  // Получить подробные данные пациента
  async getPatientDetails(id) {
    const response = await staffApi.get(`${id}/patient_details/`)
    return response.data
  },

  // === УТИЛИТЫ ===

  // Форматирование даты
  formatDate(dateString) {
    if (!dateString) return '—'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return dateString
    }
  },

  // Форматирование чисел
  formatNumber(value) {
    if (!value && value !== 0) return '—'
    return new Intl.NumberFormat('ru-RU').format(value)
  },

  // Иконки для типов пользователей
  getUserTypeIcon(userType) {
    const icons = {
      'doctor': '👨‍⚕️',
      'nurse': '👩‍⚕️',
      'patient': '👤',
      'admin': '👑'
    }
    return icons[userType] || '👤'
  },

  // Цвета для статусов
  getStatusColor(isActive) {
    return isActive ? 'text-green-600' : 'text-red-600'
  },

  // Цвета для типов пользователей
  getTypeColor(userType) {
    const colors = {
      'doctor': 'bg-blue-100 text-blue-800',
      'nurse': 'bg-green-100 text-green-800',
      'patient': 'bg-gray-100 text-gray-800',
      'admin': 'bg-purple-100 text-purple-800'
    }
    return colors[userType] || 'bg-gray-100 text-gray-800'
  },

  // Скачивание файлов
  downloadFile(data, filename) {
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },

  // === ТИПЫ ТРЕКЕРОВ ЗДОРОВЬЯ ===

  // Получить список типов трекеров
  async getHealthTrackerTypes(params = {}) {
    const response = await staffApi.get('health_tracker_types/', { params })
    return response.data
  },

  // Создать новый тип трекера
  async createTrackerType(data) {
    return staffApi.post('create_tracker_type/', data)
  },

  // Обновить тип трекера
  async updateTrackerType(data) {
    return staffApi.put(`${data.id}/update_tracker_type/`, data)
  },

  // Удалить тип трекера
  async deleteTrackerType(id) {
    return staffApi.delete(`${id}/delete_tracker_type/`)
  },

  // Health Trackers API
  async getTrackerTypes(params = {}) {
    return staffApi.get('tracker_types/', { params })
  },

  // Visit Reasons API
  async getVisitReasons(params = {}) {
    return staffApi.get('visit_reasons/', { params })
  },

  async createVisitReason(data) {
    return staffApi.post('visit_reasons/', data)
  },

  async updateVisitReason(data) {
    return staffApi.put('update_visit_reason/', data)
  },

  async deleteVisitReason(id) {
    return staffApi.delete('delete_visit_reason/', { params: { id } })
  },

  // Cancellation Reasons API
  async getCancellationReasons(params = {}) {
    return staffApi.get('cancellation_reasons/', { params })
  },

  async createCancellationReason(data) {
    return staffApi.post('cancellation_reasons/', data)
  },

  async updateCancellationReason(data) {
    return staffApi.put('update_cancellation_reason/', data)
  },

  async deleteCancellationReason(id) {
    return staffApi.delete('delete_cancellation_reason/', { params: { id } })
  },

  // ===============================================
  // НОВЫЕ ЭНДПОИНТЫ ДЛЯ /api/staff/*
  // (Для врачей и персонала в веб-приложении)
  // ===============================================

  // === APPOINTMENTS для врачей/персонала ===

  // Получить все приемы врача/персонала (БЕЗ приемов где он пациент)
  async getStaffAppointments(params = {}) {
    const response = await staffApi.get('../../staff/appointments/', { params })
    return response
  },

  // Получить приемы на сегодня (врач/персонал)
  async getStaffTodayAppointments(params = {}) {
    const response = await staffApi.get('../../staff/appointments/today/', { params })
    return response
  },

  // Получить предстоящие приемы (врач/персонал)
  async getStaffUpcomingAppointments(params = {}) {
    const response = await staffApi.get('../../staff/appointments/upcoming/', { params })
    return response
  },

  // Получить прошлые приемы (врач/персонал)
  async getStaffPastAppointments(params = {}) {
    const response = await staffApi.get('../../staff/appointments/past/', { params })
    return response
  },

  // Получить мои приемы как врач/медсестра
  async getMyStaffAppointments(params = {}) {
    const response = await staffApi.get('../../staff/appointments/my_appointments/', { params })
    return response
  },

  async getStaffAppointmentsSummary(params = {}) {
    return staffApi.get('../../staff/appointments/summary/', { params })
  },

  // Получить коммерческую аналитику врача
  async getCommercialAnalytics(params = {}) {
    const response = await staffApi.get('../../staff/appointments/commercial-analytics/', { params })
    return response
  },

  // Получить настройки причин и цен для врача (коммерческие клиники)
  async getDoctorServicesConfig(params = {}) {
    return staffApi.get('../../staff/appointments/doctor-services/', { params })
  },
  async updateDoctorServicesConfig(data) {
    return staffApi.put('../../staff/appointments/doctor-services/', data)
  },
  async getDoctorVisitReasonsConfig(params = {}) {
    const response = await staffApi.get('../../staff/appointments/doctor-visit-reasons/', { params })
    return response
  },

  // Сохранить настройки причин и цен для врача (коммерческие клиники)
  async updateDoctorVisitReasonsConfig(data) {
    const response = await staffApi.put('../../staff/appointments/doctor-visit-reasons/', data)
    return response
  },

  // Получить приемы пациентов организаций текущего врача
  async getStaffOrganizationAppointments(params = {}) {
    const response = await staffApi.get('../../staff/appointments/organization_appointments/', { params })
    return response
  },

  // Обновить статус приема
  async updateStaffAppointmentStatus(appointmentId, status) {
    const response = await staffApi.patch(
      `../../staff/appointments/${appointmentId}/`,
      { status }
    )
    return response
  },

  async cancelStaffAppointment(appointmentId, cancellationReasonId) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/cancel/`,
      { cancellation_reason_id: cancellationReasonId }
    )
    return response
  },

  // Обновить статус бронирования (для менеджера/администратора коммерческой клиники)
  async updateAppointmentBookingStatus(appointmentId, bookingStatus) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/booking-status/`,
      { booking_status: bookingStatus }
    )
    return response
  },

  async getAvailableAppointmentSlots(params = {}) {
    const response = await staffApi.get('../../patient/available-slots/available/', { params })
    return response
  },

  async rescheduleStaffAppointment(appointmentId, slotId) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/reschedule/`,
      { slot_id: slotId }
    )
    return response
  },

  // Начать прием
  async startStaffAppointment(appointmentId) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/start_appointment/`,
      {}
    )
    return response
  },

  // Завершить прием
  async completeStaffAppointment(appointmentId, data) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/complete_appointment/`,
      data
    )
    return response
  },

  // Обновить данные видеокомнаты приема
  async updateStaffAppointmentVideoRoom(appointmentId, videoRoomData) {
    const payload = {};

    // Include status if provided
    if (videoRoomData.status) {
      payload.status = videoRoomData.status;
    }

    // Only include properties that exist in the videoRoomData with correct field names
    if (videoRoomData.video_room_url || videoRoomData.url) {
      payload.video_room_url = videoRoomData.video_room_url || videoRoomData.url;
    }

    if (videoRoomData.video_room_name || videoRoomData.room_name) {
      payload.video_room_name = videoRoomData.video_room_name || videoRoomData.room_name;
    }

    // Don't make the API call if we have no data to update
    if (Object.keys(payload).length === 0) {
      return Promise.resolve({ data: null });
    }

    const response = await staffApi.patch(
      `../../staff/appointments/${appointmentId}/`,
      payload
    )
    return response
  },

  // Синхронизировать URL видеокомнаты в МИС
  async syncUrlToMIS(appointmentId) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/sync-url-to-mis/`,
      {}
    )
    return response
  },

  // Синхронизировать данные завершения приема в МИС (video_record, chat_history)
  async syncCompletionToMIS(appointmentId) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/sync-completion-to-mis/`,
      {}
    )
    return response
  },

  // Получить текущий статус доступа к медиа приема
  async getAppointmentMediaAccess(appointmentId) {
    const response = await staffApi.get(
      `../../staff/appointments/${appointmentId}/media-access/`
    )
    return response
  },

  // Отправить пациенту запрос на доступ к видео/чату приема
  async requestAppointmentMediaAccess(appointmentId) {
    const response = await staffApi.post(
      `../../staff/appointments/${appointmentId}/media-access/request/`,
      {}
    )
    return response
  },

  // Получить записи видео приема через защищенный backend endpoint
  async getAppointmentMediaRecords(appointmentId) {
    const response = await staffApi.get(
      `../../staff/appointments/${appointmentId}/media-records/`
    )
    return response
  },

  // Получить историю чата приема через защищенный backend endpoint
  async getAppointmentMediaChat(appointmentId) {
    const response = await staffApi.get(
      `../../staff/appointments/${appointmentId}/media-chat/`
    )
    return response
  },

  // === HEALTH TRACKERS для врачей/персонала ===

  // Получить трекеры пациентов клиники (БЕЗ личных трекеров врача)
  async getStaffHealthTrackers(filters = {}) {
    const response = await staffApi.get('../../staff/health-trackers/', { params: filters })
    return response
  },

  // Получить трекеры конкретного пациента
  async getPatientHealthTrackers(patientId) {
    const response = await staffApi.get('../../staff/health-trackers/', {
      params: { patient: patientId }
    })
    return response
  },

  // Получить статистику по трекерам пациента
  async getPatientHealthTrackerStats(patientId, metricType) {
    const response = await staffApi.get('../../staff/health-trackers/list_records/', {
      params: {
        patient: patientId,
        metric_type: metricType,
      }
    })
    return response
  },

  // === SURVEYS для врачей/персонала ===

  // Получить ответы на опросы конкретного пациента  
  async getPatientSurveyResponses(patientId) {
    const response = await staffApi.get(`../../surveys/responses/`, {
      params: { patient: patientId }
    })
    return response
  },

  // === ИНДИКАТОРЫ ПРЕВЕНЦИИ СУИЦИДОВ ===
  async getIndicators(params = {}) {
    const response = await staffApi.get('indicators/', { params })
    return response
  },

  async exportIndicators(params = {}) {
    const response = await staffApi.get('indicators/export/', {
      params,
      responseType: 'blob'
    })
    return response
  },

  // === ВРАЧИ ПО ШКОЛАМ ===
  async getUltrasoundAccount(doctorId) {
    return staffApi.get(`${doctorId}/ultrasound_account/`)
  },

  async prepareUltrasoundAccount(doctorId) {
    return staffApi.post(`${doctorId}/ultrasound_account/`, {}, { timeout: 180000 })
  },

  async getSchoolDoctors(params = {}) {
    const response = await staffApi.get('school-doctors/', { params })
    return response
  },

  async exportSchoolDoctors(params = {}) {
    const response = await staffApi.get('school-doctors/export/', {
      params,
      responseType: 'blob'
    })
    return response
  },
}
